import React, { useState, useEffect, useMemo, useRef, Suspense } from "react";
import { Select, MenuItem } from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import Tooltip from "@material-ui/core/Tooltip";
import FontIcon from "react-md/lib/FontIcons";
import moment from "moment";
import { Link } from "react-router-dom";

import "../styles/ltrDetail.scss";
import LTRService from "../service/LTRService";
import callSession from "../call/callSession";
import CallDetailsDialog from "../call/CallDetailsDialog";
import CallAudit, { nameOf } from "./detail/CallAudit";
import RequiredAction from "./detail/RequiredAction";
import ActivityLog from "./detail/ActivityLog";
import ConvertCallDialog, { AppointmentCreatedDialog } from "./detail/ConvertCallDialog";
import DocumentLink from "./detail/DocumentLink";
import PatientService from "modules/patient/service/PatientService";
import {
  AssignActionDialog,
  FollowUpTaskDialog,
  CloseCaseDialog,
  CaseClosedDialog,
  ClosureDetailsDialog,
  ComingSoonDialog,
  RemarkDialog,
} from "./LTRCaseDialogs";
import RiskStratificationService from "../../risk-stratification/service/RiskStratificationService";
import AIEvaluationDialog from "../../risk-stratification/components/evaluation/AIEvaluationDialog";
import EvaluationSummaryDialog from "../../risk-stratification/components/evaluation/EvaluationSummaryDialog";
import {
  RiskBadge,
  KeyDriversList,
  RecommendedActionsList,
} from "../../risk-stratification/components/evaluation/RiskSummaryBlocks";
import {
  getTagClass,
  getTrendIcon,
  getTrendClass,
  getValueColorClass,
  getPatientAge,
  getPatientGender,
  AUDIT_EVENT_SUMMARY_UPDATED,
  EVALUATION_STATUS_CORRECT,
  EVALUATION_STATUS_INCORRECT,
} from "../../risk-stratification/utils/riskUtils";
import AuthenticationService from "service/auth/AuthenticationService";
import EventService from "service/event/EventService";
import {
  STATUS_NEW_LEAD,
  STATUS_CLOSED,
  STATUS_LABEL_MAP,
  OUTCOME_LABEL_MAP,
  ACTION_LABEL_MAP,
  ROLE_COORDINATOR,
  ROLE_DOCTOR,
  ROLE_LABEL_MAP,
  LTR_EVALUATION_CORRECT,
  LTR_EVALUATION_INCORRECT,
} from "../constants";
// The patient's user type, as the Appointments dashboard's filter labels and
// colours it.
import { USER_TYPE_OPTIONS as PROFILE_TYPE_OPTIONS } from "../../navigo-module/components/UserTypeFilter";
import backArrowIcon from "../../../../../assets/icons/back_left_arrow_orange.png";
import arrowUpRed from "../../../../../assets/icons/arrow_up_red.png";
import circleCheckIcon from "../../../../../assets/icons/ circle_check_icon.png";
import circleCrossIcon from "../../../../../assets/icons/circlw_cross_icon.png";

// Bridge's progress-note modal brings global styles with it, so it is only
// loaded when "+ Assign task" is used.
const LTRProgressNote = React.lazy(() => import("./detail/LTRProgressNote"));

const ltrService = new LTRService();
const riskService = new RiskStratificationService();
const patientService = new PatientService();
const authenticationService = new AuthenticationService();

const DASHBOARD_PATH = "/miscellaneous/ltr/dashboard";
// How long to keep looking for a newly published Rx after "Create Rx".
const RX_WATCH_MS = 30 * 60 * 1000;
const RX_POLL_MS = 15000;

// Adapts the callback-style services to promises so dialogs can await them.
const call = (fn, ...args) => new Promise((resolve, reject) => fn(...args, resolve, reject));

// A zero ObjectID is what Go serialises for "not set".
const isSetId = (id) => !!id && !/^0+$/.test(String(id));

const CLINICAL_GROUPS = [
  { type: "vital", title: "Vital Signs" },
  { type: "labTest", title: "Lab Values" },
  { type: "diagnosis", title: "Diagnosis History" },
  { type: "chiefComplaint", title: "Chief Complaints" },
  { type: "hospitalisation", title: "Hospitalisation History" },
];

// Mirrors the risk detail page. Prefers the backend's dataCoverage (what was
// actually fetched) and falls back to clinicalData for older records.
const DATA_COVERAGE = [
  { label: "Lab reports", key: "labTest" },
  { label: "Vital signs", key: "vital" },
  { label: "Diagnosis history", key: "diagnosis" },
  { label: "Family history", key: "familyHistory" },
  { label: "Chief complaints", key: "chiefComplaint" },
  { label: "Medication records", key: "medicine" },
  { label: "Hospitalisation history", key: "hospitalisation" },
];

// The closed status pill reads "Closed · Successful".
const CLOSED_PILL_LABEL = {
  1: "Successful",
  2: "Unsuccessful",
  3: "False positive",
  4: "Not interested",
};

const fmtDateTime = (d) => (d ? moment(d).format("D MMM YYYY, h:mm A") : "-");
const days = (from, to) => Math.max(0, moment(to || undefined).startOf("day").diff(moment(from).startOf("day"), "days"));

function DateTimeStack({ value }) {
  if (!value) return <span>-</span>;
  const m = moment(value);
  if (!m.isValid()) return <span>-</span>;
  return (
    <span className="ltr-datetime">
      <span className="ltr-datetime-date">{m.format("D MMM YYYY")}</span>
      <span className="ltr-datetime-time">{m.format("h:mm A")}</span>
    </span>
  );
}

function Fact({ label, children }) {
  return (
    <div className="ltr-fact">
      <span className="ltr-fact-label">{label}</span>
      <div className="ltr-fact-value">{children}</div>
    </div>
  );
}

function EditButton({ onClick, label }) {
  return (
    <button type="button" className="ltr-edit-btn" onClick={onClick} aria-label={label}>
      <FontIcon>edit</FontIcon>
    </button>
  );
}

function ClinicalCard({ item }) {
  const trend = getTrendIcon(item.tag);
  return (
    <div className="ltr-clinical-card">
      <div className="ltr-clinical-card-head">
        <span className="ltr-clinical-label">{item.paramName}</span>
        {trend !== "trending_flat" && (
          <img
            src={arrowUpRed}
            alt={trend === "trending_up" ? "trending up" : "trending down"}
            className={`ltr-clinical-trend ${getTrendClass(item.tag)}`}
            style={trend === "trending_down" ? { transform: "rotate(180deg)" } : undefined}
          />
        )}
      </div>
      <div className="ltr-clinical-value-row">
        <span className={`ltr-clinical-value ${getValueColorClass(item.tag)}`}>
          {item.value} {item.unit}
        </span>
        {item.tag && <span className={`ltr-clinical-tag ${getTagClass(item.tag)}`}>{item.tag}</span>}
      </div>
      {item.recordDate && (
        <div className="ltr-clinical-date">Recorded on: {moment(item.recordDate).format("D MMM YYYY")}</div>
      )}
    </div>
  );
}

function LTRDetail(props) {
  const caseId = props.data?.id;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [caseRecord, setCaseRecord] = useState(null);
  const [riskRecord, setRiskRecord] = useState(null);
  const [riskError, setRiskError] = useState("");
  const [riskAudits, setRiskAudits] = useState([]);
  const [ltrAudits, setLtrAudits] = useState([]);
  const [patient, setPatient] = useState(null);
  const [heroVisible, setHeroVisible] = useState(true);

  // Dialogs
  const [evalDialog, setEvalDialog] = useState(null); // { mode, record }
  const [summaryAuditId, setSummaryAuditId] = useState(undefined);
  const [prevSummaries, setPrevSummaries] = useState(null);
  const [callDetailsOpen, setCallDetailsOpen] = useState(false);
  const [convertCall, setConvertCall] = useState(null);
  const [createdAppointment, setCreatedAppointment] = useState(null);
  const [unlinked, setUnlinked] = useState(null); // booked upstream, not yet on the case
  const [assignAction, setAssignAction] = useState(null); // { appointment, withFollowUp }
  // The follow-up / assign dialog: null when shut, else "followUp" (the pencil
  // on Follow up date) or "assign" (an unassigned case's Assign button).
  const [followUpOpen, setFollowUpOpen] = useState(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closedOpen, setClosedOpen] = useState(false);
  const [closureOpen, setClosureOpen] = useState(false);
  const [comingSoon, setComingSoon] = useState(null); // "chat" | "mail"
  const [remarkFor, setRemarkFor] = useState(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [rxWatch, setRxWatch] = useState(null); // { appointmentId, baselineId, since }
  const [retryingProgramme, setRetryingProgramme] = useState(false);

  const containerRef = useRef(null);
  const heroRef = useRef(null);
  const closureRef = useRef(null);
  const rxCheckingRef = useRef(false);
  // The case this page last adopted, so the call-session subscriber can tell
  // the pill's updates from the page's own.
  const caseRef = useRef(null);

  // LTR doctors sign in with a doctor profile, coordinators with an employee one.
  const profile = authenticationService.getUserProfile() || {};
  const userProfile = profile.employeeProfile || profile.doctorProfile || {};
  const currentUserName =
    [userProfile.firstName, userProfile.lastName].filter(Boolean).join(" ") || userProfile.name || "";

  const applyCase = (c) => {
    if (!c) return;
    caseRef.current = c;
    setCaseRecord(c);
    callSession.syncCase(c);
  };

  const loadAudits = () =>
    call(ltrService.fetchCaseAudits, caseId)
      .then((res) => setLtrAudits(Array.isArray(res) ? res : []))
      .catch((err) => {
        console.error("Error fetching LTR activity log:", err);
        setLtrAudits([]);
      });

  const loadRisk = (riskRecordId) => {
    call(riskService.fetchDetail, riskRecordId)
      .then((res) => {
        setRiskRecord(res || null);
        setRiskError(res ? "" : "The AI risk summary for this case was not found.");
      })
      .catch((err) => {
        console.error("Error fetching risk record:", err);
        setRiskError(err?.error || err?.message || "Could not load the AI risk summary.");
      });
    call(riskService.fetchAudits, riskRecordId)
      .then((res) => setRiskAudits(Array.isArray(res) ? res : []))
      .catch(() => setRiskAudits([]));
  };

  useEffect(() => {
    if (!caseId) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    setCaseRecord(null);
    setRiskRecord(null);
    setRiskError("");
    setRiskAudits([]);
    setPatient(null);

    call(ltrService.fetchCase, caseId)
      .then((c) => {
        if (cancelled) return;
        if (!c) {
          setLoadError("Case not found");
          setLoading(false);
          return;
        }
        applyCase(c);
        setLoading(false);
        if (isSetId(c.riskRecordId)) loadRisk(c.riskRecordId);
        else setRiskError("This case is not linked to a risk assessment.");
        if (c.patientId) {
          call(ltrService.fetchPatient, c.patientId)
            .then((p) => !cancelled && setPatient(p || null))
            .catch((err) => console.error("Error fetching patient:", err));
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err?.message || "Case not found");
        setLoading(false);
      });
    loadAudits();
    // The Rx PDFs are token-gated: mint the doc token now, in the background,
    // so a "View document" click can append it without a fetch of its own.
    patientService.generateDocToken(
      () => {},
      () => {},
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  // Every case mutation returns the updated case: adopt it, refresh the log,
  // and tell the dashboard its rows are stale.
  const mutate = (promise, successMessage) =>
    promise.then((updated) => {
      applyCase(updated);
      loadAudits();
      if (props.markDashboardDirty) props.markDashboardDirty();
      if (successMessage) EventService.emit("showError", { message: successMessage });
      return updated;
    });

  const goBack = () => {
    if (props.history) props.history.push(DASHBOARD_PATH);
  };

  // ---- pinned header -------------------------------------------------------
  useEffect(() => {
    if (!heroRef.current || !containerRef.current || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(([entry]) => setHeroVisible(entry.isIntersecting), {
      root: containerRef.current,
      threshold: 0,
    });
    observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, [loading]);

  // ---- in-call pill requests ----------------------------------------------
  // `inClick` is false when the request is picked up after navigating here
  // from another page: the Rx editor tab cannot be opened outside the click,
  // so the doctor is pointed at the Create Rx button instead.
  const runPillAction = (action, c, inClick) => {
    if (!action || !c) return;
    const sessionCall = (c.calls || []).find((x) => x.callId === action.callId);
    const appt = (c.appointments || []).find((a) => a.callId === action.callId);
    if (action.kind === "appointment" && sessionCall) setConvertCall(sessionCall);
    else if (action.kind === "rx" && appt) {
      if (inClick) startRx(appt);
      else EventService.emit("showError", { message: "Use Create Rx on the appointment below to open the editor." });
    } else if (action.kind === "action" && appt) setAssignAction({ appointment: appt, withFollowUp: false });
    else if (action.kind === "followUp") setFollowUpOpen("followUp");
  };

  useEffect(() => {
    if (!caseRecord) return undefined;
    runPillAction(callSession.takeAction(caseRecord.id), caseRecord, false);
    return callSession.onAction(() => runPillAction(callSession.takeAction(caseRecord.id), caseRecord, true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseRecord]);

  // The pill updates the case itself (connect, hang-up). Adopt its newer copy
  // so the call audit and log follow without a reload.
  useEffect(
    () =>
      callSession.subscribe((s) => {
        const fromPill = s && s.caseRecord;
        if (!fromPill || fromPill.id !== caseId || fromPill === caseRef.current) return;
        caseRef.current = fromPill;
        setCaseRecord(fromPill);
        loadAudits();
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [caseId],
  );

  // ---- Rx: open the editor, then pick up what was published ---------------
  // Must run inside the click that asked for it: the editor tab is opened
  // synchronously so pop-up blockers allow it.
  const startRx = (appt) => {
    if (!caseRecord) return;
    const since = Date.now();
    // The patient record's Create Rx editor. ?appointmentId= is saved with the
    // prescription, which is what ties it to this consultation.
    const tab = window.open(
      `${window.location.origin}/patient-record/${caseRecord.patientId}/prescription?appointmentId=${appt.appointmentId}`,
      "_blank",
    );
    if (tab) tab.opener = null;
    else EventService.emit("showError", { message: "Allow pop-ups for Bridge to open the prescription editor." });
    // The Rx that is latest right now is the baseline; only a different one
    // published after this moment is taken as this appointment's Rx.
    call(ltrService.fetchLatestPrescription, caseRecord.patientId)
      .then((rx) =>
        setRxWatch({
          appointmentId: appt.appointmentId,
          baselineId: rx && rx.id != null ? String(rx.id) : null,
          baselineKnown: true,
          since,
        }),
      )
      .catch(() => setRxWatch({ appointmentId: appt.appointmentId, baselineId: null, baselineKnown: false, since }));
  };

  // When the prescription carries a timestamp, whether it postdates "Create Rx".
  // null when there is no usable timestamp.
  const rxIsAfter = (rx, since) => {
    const raw = rx && (rx.dateUpdated || rx.dateCreated || rx.updatedAt || rx.createdAt);
    const t = raw ? new Date(raw).getTime() : NaN;
    return Number.isNaN(t) ? null : t >= since - 2 * 60 * 1000;
  };

  const checkRx = (manual) => {
    const watch = rxWatch;
    if (!watch || !caseRecord || rxCheckingRef.current) return;
    rxCheckingRef.current = true;
    const done = () => {
      rxCheckingRef.current = false;
    };
    call(ltrService.fetchLatestPrescription, caseRecord.patientId)
      .then((rx) => {
        const id = rx && rx.id != null ? String(rx.id) : null;
        const after = rxIsAfter(rx, watch.since);
        // A different Rx from the baseline — or, with no baseline, one that is
        // provably newer than "Create Rx" — and never one dated before it.
        const fresh = !!id && (watch.baselineKnown ? id !== watch.baselineId : after === true) && after !== false;
        const sameAppointment = !rx || rx.appointmentId == null || Number(rx.appointmentId) === watch.appointmentId;
        if (!fresh || !sameAppointment) {
          if (manual) EventService.emit("showError", { message: "No new prescription found yet. Publish it in the editor tab." });
          return null;
        }
        // The PDF is mapped to the appointment by the prescription service;
        // take it when it is there, link without it otherwise.
        return call(ltrService.fetchAppointmentDocuments, watch.appointmentId)
          .catch(() => null)
          .then((res) => {
            const docs = (res && Array.isArray(res.documents) ? res.documents : []).slice();
            docs.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
            const doc = docs.find((d) => d.type === 9) || docs[0];
            return mutate(
              call(ltrService.linkPrescription, caseRecord.id, {
                appointmentId: watch.appointmentId,
                prescriptionId: id,
                prescriptionUrl: (doc && doc.url) || "",
              }),
              `Rx ${id} linked to appointment ${watch.appointmentId}`,
            ).then(() => setRxWatch(null));
          });
      })
      .catch((err) => {
        console.error("Error checking for the published Rx:", err);
        if (manual) EventService.emit("showError", { message: err?.message || "Could not check for the prescription." });
      })
      .finally(done);
  };

  useEffect(() => {
    if (!rxWatch) return undefined;
    const onFocus = () => document.visibilityState === "visible" && checkRx(false);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    const poll = setInterval(() => {
      if (Date.now() - rxWatch.since > RX_WATCH_MS) setRxWatch(null);
      else checkRx(false);
    }, RX_POLL_MS);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      clearInterval(poll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rxWatch]);

  // ---- derived -------------------------------------------------------------
  const evaluationVersions = useMemo(
    () => riskAudits.filter((a) => a.eventType === AUDIT_EVENT_SUMMARY_UPDATED),
    [riskAudits],
  );

  // Role per employee: from the calls they placed and the assignments made.
  const roleById = useMemo(() => {
    const map = {};
    (caseRecord?.calls || []).forEach((c) => {
      if (c.callerId && c.callerRole) map[c.callerId] = c.callerRole;
    });
    [...ltrAudits]
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      .forEach((a) => {
        if (a.subjectEmployeeId && a.metadata?.role) map[a.subjectEmployeeId] = a.metadata.role;
      });
    if (caseRecord?.assignedToEmployeeId && caseRecord.assignedToRole) {
      map[caseRecord.assignedToEmployeeId] = caseRecord.assignedToRole;
    }
    return map;
  }, [ltrAudits, caseRecord]);

  if (loading) {
    return (
      <div className="ltr-detail-container">
        <div className="ltr-detail-card ltr-detail-placeholder">Loading case...</div>
      </div>
    );
  }

  if (!caseRecord) {
    return (
      <div className="ltr-detail-container">
        <div className="ltr-detail-card ltr-detail-placeholder">
          <img src={backArrowIcon} alt="back" className="ltr-back" onClick={goBack} />
          {loadError || "Case not found"}
        </div>
      </div>
    );
  }

  const c = caseRecord;
  const names = c.employeeNames || {};
  const isClosed = c.status === STATUS_CLOSED;
  const patientName = patient ? `${patient.firstName || ""} ${patient.lastName || ""}`.trim() : "";
  const age = getPatientAge(patient);
  const patientMeta = [getPatientGender(patient), age != null ? `${age} yr` : ""].filter(Boolean).join(" · ");
  // The patient's health record, in the same tab like every patient link on
  // Bridge.
  const patientUrl = `/patient-record/${c.patientId}`;
  const followUpOverdue = !isClosed && c.followUpDate && moment(c.followUpDate).isBefore(moment());
  // The user type is the patient's profile type on the patient record this page
  // loaded; nothing shows until it is in.
  const userType =
    patient && patient.profileType != null
      ? PROFILE_TYPE_OPTIONS.find((o) => o.value === Number(patient.profileType))
      : null;
  const activeSession = callSession.get();
  const busyOnAnotherCall = !!activeSession && activeSession.phase !== "ended";
  const appointments = c.appointments || [];
  const latestAppointment = appointments[appointments.length - 1] || null;
  const latestActions = (latestAppointment && latestAppointment.actions) || c.actions || [];
  const durationLabel = isClosed
    ? `${days(c.createdAt, c.closedAt)} day${days(c.createdAt, c.closedAt) === 1 ? "" : "s"}`
    : `Day ${days(c.createdAt) + 1}`;

  // ---- handlers ------------------------------------------------------------

  const handleCallPlaced = (updated) => {
    setCallDetailsOpen(false);
    applyCase(updated);
    loadAudits();
    const placed = (updated.calls || [])[updated.calls.length - 1];
    if (placed) {
      callSession.start({
        caseId: updated.id,
        ltrId: updated.ltrId,
        patientId: updated.patientId,
        patientName,
        ltrCall: placed,
        caseRecord: updated,
      });
    }
    if (props.markDashboardDirty) props.markDashboardDirty();
  };

  const linkBooking = (link) =>
    mutate(call(ltrService.linkAppointment, c.id, link), `Appointment ${link.appointmentId} created`).then(
      (updated) => {
        setUnlinked(null);
        setConvertCall(null);
        setCreatedAppointment(
          (updated.appointments || []).find((a) => a.appointmentId === link.appointmentId) || link,
        );
      },
    );

  const handleBooked = ({ link }) =>
    linkBooking(link).catch((err) => {
      // Booked upstream but not on the case: keep the booking so it can be
      // linked again rather than booked twice.
      setConvertCall(null);
      setUnlinked({ link, message: err?.message || "" });
    });

  const handleSetActions = (appt, { actions, note, assigneeId, followUpDate }) =>
    mutate(
      call(ltrService.saveActions, c.id, { appointmentId: appt.appointmentId, actions, note }),
      "Actions set",
    ).then(() =>
      assigneeId && assigneeId !== c.assignedToEmployeeId
        ? mutate(
            call(ltrService.assignCase, c.id, {
              employeeId: assigneeId,
              role: ROLE_COORDINATOR,
              task: note,
              followUpDate: followUpDate || null,
            }),
          )
        : followUpDate
        ? mutate(call(ltrService.saveFollowUp, c.id, followUpDate))
        : null,
    );

  const handleFollowUpTask = (body) =>
    mutate(call(ltrService.assignCase, c.id, body), "Follow-up set").then(() => {
      const s = callSession.get();
      if (s && s.caseId === c.id) callSession.update({ followUpDone: true });
    });

  const handleEvaluationSaved = (payload) => {
    setEvalDialog(null);
    loadRisk(c.riskRecordId);
    if (isClosed) return;
    mutate(
      call(ltrService.saveEvaluation, c.id, {
        evaluationStatus:
          payload.evaluationStatus === EVALUATION_STATUS_INCORRECT ? LTR_EVALUATION_INCORRECT : LTR_EVALUATION_CORRECT,
        updatedRiskLevel: payload.updatedRiskLevel,
        note: payload.evaluationReason,
      }),
      "Evaluation saved",
    ).catch((err) => {
      console.error("Error mirroring evaluation onto LTR case:", err);
      EventService.emit("showError", {
        message: `Evaluation saved, but the case could not be updated: ${err?.message || "please retry"}`,
      });
    });
  };

  const openPreviousSummaries = () => {
    if (!c.patientId) return;
    setPrevSummaries({ loading: true, items: [] });
    riskService.fetchPatientHistory(
      c.patientId,
      c.riskRecordId,
      (res) => setPrevSummaries({ loading: false, items: Array.isArray(res) ? res : [] }),
      () => setPrevSummaries({ loading: false, items: [] }),
    );
  };

  const openPreviousSummary = (item) => {
    setPrevSummaries(null);
    call(riskService.fetchDetail, item.id)
      .then((rec) => rec && setEvalDialog({ mode: "view", record: rec }))
      .catch((err) => EventService.emit("showError", { message: err?.error || "Could not load that AI summary." }));
  };

  // ---- hero ----------------------------------------------------------------

  const statusOptions = [
    { value: c.status, label: c.status === STATUS_NEW_LEAD ? "New lead" : "Case in progress" },
    { value: STATUS_CLOSED, label: "Close" },
  ];

  const statusControl = isClosed ? (
    <button type="button" className="ltr-closed-pill" onClick={() => setClosureOpen(true)}>
      <FontIcon>check_circle</FontIcon>
      Closed · {CLOSED_PILL_LABEL[c.closureOutcome] || "Closed"}
    </button>
  ) : (
    <Select
      value={c.status}
      onChange={(e) => e.target.value === STATUS_CLOSED && setCloseOpen(true)}
      disableUnderline
      className={`ltr-status-select ${c.status === STATUS_NEW_LEAD ? "new-lead" : "in-progress"}`}
      renderValue={() => STATUS_LABEL_MAP[c.status]}
    >
      {statusOptions.map((o) => (
        <MenuItem key={o.value} value={o.value} className="ltr-status-option">
          <span>{o.label}</span>
          <FontIcon>{o.value === c.status ? "check" : "chevron_right"}</FontIcon>
        </MenuItem>
      ))}
    </Select>
  );

  const hero = (
    <section className="ltr-detail-card ltr-hero" ref={heroRef}>
      <div className="ltr-hero-top">
        <div className="ltr-hero-identity">
          {userType && (
            <span className="ltr-lead-pill" style={userType.style}>
              {userType.label}
            </span>
          )}
          <div className="ltr-hero-name-row">
            <img src={backArrowIcon} alt="back" className="ltr-back" onClick={goBack} />
            <h2 className="ltr-hero-name">{patientName || `PID-${c.patientId}`}</h2>
            {patientMeta && <span className="ltr-hero-meta">{patientMeta}</span>}
          </div>
        </div>
        <div className="ltr-hero-status">
          {statusControl}
          <Tooltip arrow placement="bottom" title="Changes on this case are saved as you make them">
            <span>
              <button type="button" className="ltr-btn primary" disabled>
                Save
              </button>
            </span>
          </Tooltip>
        </div>
      </div>

      <div className="ltr-hero-facts">
        <Fact label="Patient ID">
          <Link to={patientUrl} className="ltr-link">
            {c.patientId}
          </Link>
        </Fact>
        <Fact label="LTR ID">{c.ltrId || "-"}</Fact>
        <Fact label="Assigned to">
          <span className="ltr-fact-inline">
            {c.assignedToName || "-"}
            {/* Intake leaves a case without contact consent unassigned; this
                is how someone takes it on. */}
            {!isClosed && !c.assignedToEmployeeId && (
              <button type="button" className="ltr-text-btn" onClick={() => setFollowUpOpen("assign")}>
                Assign
              </button>
            )}
          </span>
        </Fact>
        <Fact label="Contact consent">
          <Tooltip
            arrow
            placement="bottom"
            title={
              c.consentGiven
                ? "The patient agreed to be contacted on an appointment in the last two years"
                : "No contact consent on the patient's appointments — not auto-assigned"
            }
          >
            <span>{c.consentGiven ? "Yes" : "No"}</span>
          </Tooltip>
        </Fact>
        <Fact label="Call attempts">{Math.max(c.callCount || 0, (c.calls || []).length)}</Fact>
        <Fact label="Case start date">
          <DateTimeStack value={c.createdAt} />
        </Fact>
        {isClosed && (
          <Fact label="Case end date">
            <DateTimeStack value={c.closedAt} />
          </Fact>
        )}
        <Fact label="Case duration">
          {isClosed ? durationLabel : <span className="ltr-duration-pill">{durationLabel}</span>}
        </Fact>
        <Fact label="Follow up date">
          <span className="ltr-fact-inline">
            <span className={followUpOverdue ? "ltr-overdue" : ""}>
              <DateTimeStack value={c.followUpDate} />
            </span>
            {!isClosed && <EditButton label="Update follow up date" onClick={() => setFollowUpOpen("followUp")} />}
          </span>
        </Fact>
        <div className="ltr-hero-cta">
          <Tooltip
            arrow
            placement="top"
            title={busyOnAnotherCall ? "Hang up the current call first" : isClosed ? "This case is closed" : ""}
          >
            <span>
              <button
                type="button"
                className="ltr-btn primary"
                disabled={isClosed || !patient || busyOnAnotherCall}
                onClick={() => setCallDetailsOpen(true)}
              >
                <FontIcon>call</FontIcon>
                Call patient
              </button>
            </span>
          </Tooltip>
          <button type="button" className="ltr-icon-btn" onClick={() => setComingSoon("chat")} aria-label="Chat with patient">
            <FontIcon>chat_bubble_outline</FontIcon>
          </button>
          <button type="button" className="ltr-icon-btn" onClick={() => setComingSoon("mail")} aria-label="E-mail patient">
            <FontIcon>mail_outline</FontIcon>
          </button>
        </div>
      </div>
    </section>
  );

  const pinnedHeader = (
    <div className={`ltr-pinned-header${heroVisible ? "" : " visible"}`} aria-hidden={heroVisible}>
      <div className="ltr-pinned-bar">
        <img src={backArrowIcon} alt="back" className="ltr-back" onClick={goBack} />
        <span className="ltr-hero-name small">{patientName || `PID-${c.patientId}`}</span>
        {patientMeta && <span className="ltr-hero-meta">{patientMeta}</span>}
        <Link to={patientUrl} className="ltr-link">
          PID {c.patientId}
        </Link>
        <span className="ltr-strong">{c.ltrId}</span>
      </div>
    </div>
  );

  // ---- AI summary ----------------------------------------------------------

  const latestEvaluation = evaluationVersions.length
    ? [...evaluationVersions].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))[evaluationVersions.length - 1]
    : null;
  // A Correct verdict leaves the AI summary standing. An Incorrect one
  // replaces it here with the doctor's version, the original a click away.
  const corrected =
    riskRecord?.evaluationStatus === EVALUATION_STATUS_INCORRECT &&
    !!(riskRecord.evaluatorSummary || riskRecord.keyRiskDriversHtml || riskRecord.evaluatorRecommendations);
  const evaluated =
    riskRecord?.evaluationStatus === EVALUATION_STATUS_CORRECT ||
    riskRecord?.evaluationStatus === EVALUATION_STATUS_INCORRECT;
  const updatedOn = corrected && latestEvaluation ? latestEvaluation.createdAt : riskRecord?.completedAt || riskRecord?.triggeredAt;

  const aiSummary = riskRecord ? (
    <section className="ltr-detail-card">
      <div className="ltr-card-head">
        <div className="ltr-card-title-row">
          <h3>{corrected ? "Evaluator Summary" : "AI Risk Summary"}</h3>
          <RiskBadge level={corrected && riskRecord.updatedRiskLevel ? riskRecord.updatedRiskLevel : riskRecord.riskLevel} />
          {updatedOn && (
            <span className="ltr-card-meta">
              Updated on: {moment(updatedOn).format("D MMM YYYY")}
            </span>
          )}
        </div>
        <div className="ltr-card-actions">
          <button type="button" className="ltr-btn outline" onClick={openPreviousSummaries}>
            View previous AI summary
          </button>
          {corrected && (
            <button
              type="button"
              className="ltr-btn outline"
              onClick={() => setEvalDialog({ mode: "view", record: riskRecord })}
            >
              View AI summary
            </button>
          )}
          {!isClosed && (
            <button
              type="button"
              className="ltr-btn outline"
              onClick={() => setEvalDialog({ mode: "evaluate", record: riskRecord })}
            >
              {evaluated ? "Re-evaluate" : "Evaluate AI risk summary"}
            </button>
          )}
        </div>
      </div>
      {corrected ? (
        <>
          {riskRecord.evaluatorSummary && (
            <div className="ltr-boxed ltr-eval-html" dangerouslySetInnerHTML={{ __html: riskRecord.evaluatorSummary }} />
          )}
          {riskRecord.keyRiskDriversHtml && (
            <>
              <h4 className="ltr-subtitle">Key Risk Drivers</h4>
              <div className="ltr-boxed ltr-eval-html" dangerouslySetInnerHTML={{ __html: riskRecord.keyRiskDriversHtml }} />
            </>
          )}
        </>
      ) : (
        <>
          {riskRecord.riskSummary && <p className="ltr-summary-text">{riskRecord.riskSummary}</p>}
          {Array.isArray(riskRecord.keyDrivers) && riskRecord.keyDrivers.length > 0 && (
            <>
              <div className="ltr-divider" />
              <h4 className="ltr-subtitle">Key Risk Drivers</h4>
              <KeyDriversList drivers={riskRecord.keyDrivers} />
            </>
          )}
        </>
      )}
    </section>
  ) : (
    <section className="ltr-detail-card">
      <div className="ltr-card-title-row">
        <h3>AI Risk Summary</h3>
      </div>
      <p className="ltr-muted">{riskError || "Loading AI risk summary..."}</p>
    </section>
  );

  const recommendedSection = corrected
    ? riskRecord.evaluatorRecommendations && (
        <section className="ltr-detail-card">
          <h3 className="ltr-card-title">Evaluator recommendations</h3>
          <div className="ltr-eval-html" dangerouslySetInnerHTML={{ __html: riskRecord.evaluatorRecommendations }} />
        </section>
      )
    : riskRecord &&
      Array.isArray(riskRecord.recommendedActions) &&
      riskRecord.recommendedActions.length > 0 && (
        <section className="ltr-detail-card">
          <h3 className="ltr-card-title">Recommended Actions</h3>
          <RecommendedActionsList actions={riskRecord.recommendedActions} />
        </section>
      );

  const clinicalData = riskRecord?.clinicalData || [];
  const clinicalGroups = CLINICAL_GROUPS.map((g) => ({
    ...g,
    items: clinicalData.filter((d) => d.type === g.type),
  })).filter((g) => g.items.length > 0);

  const clinicalSection = riskRecord && clinicalGroups.length > 0 && (
    <section className="ltr-detail-card">
      <h3 className="ltr-card-title">Clinical Data</h3>
      {clinicalGroups.map((g) => (
        <div key={g.type} className="ltr-clinical-group">
          <h4 className="ltr-subtitle">{g.title}</h4>
          <div className="ltr-clinical-grid">
            {g.items.map((item, idx) => (
              <ClinicalCard key={idx} item={item} />
            ))}
          </div>
        </div>
      ))}
    </section>
  );

  const coverageSection = riskRecord && (
    <section className="ltr-detail-card">
      <h3 className="ltr-card-title">Data Coverage</h3>
      <div className="ltr-coverage-row">
        {DATA_COVERAGE.map((item) => {
          const available = riskRecord.dataCoverage
            ? !!riskRecord.dataCoverage[item.key]
            : clinicalData.some((d) => d.type === item.key);
          return (
            // Not "active": user/employee/whitelisting ship a global .active
            // with a green fill, which painted every covered item as a box.
            <div key={item.key} className={`ltr-coverage-item${available ? " ltr-coverage-item--covered" : ""}`}>
              <img src={available ? circleCheckIcon : circleCrossIcon} alt="" />
              <span>{item.label}</span>
            </div>
          );
        })}
      </div>
    </section>
  );

  // Action & closure (02.4) — what the case closed on.
  const closureSection = isClosed && (
    <section className="ltr-detail-card" ref={closureRef}>
      <h3 className="ltr-card-title">Action &amp; closure</h3>
      <div className="ltr-closure-grid">
        <div>
          <span className="ltr-fact-label plain">Action</span>
          <div className="ltr-chip-row">
            {latestActions.length ? (
              latestActions.map((a) => (
                <span key={a} className="ltr-grey-pill">
                  {ACTION_LABEL_MAP[a]}
                </span>
              ))
            ) : (
              <span className="ltr-muted">-</span>
            )}
          </div>
        </div>
        <div>
          <span className="ltr-fact-label plain">Set by</span>
          <div className="ltr-strong">
            {latestAppointment && latestAppointment.actionsSetBy ? nameOf(names, latestAppointment.actionsSetBy) : "-"}
          </div>
        </div>
        <div>
          <span className="ltr-fact-label plain">Set on</span>
          <div className="ltr-strong">{fmtDateTime(latestAppointment && latestAppointment.actionsSetAt)}</div>
        </div>
        <div>
          <span className="ltr-fact-label plain">Close proof</span>
          <div className="ltr-strong">{c.closureReason || OUTCOME_LABEL_MAP[c.closureOutcome] || "-"}</div>
        </div>
        <div>
          <span className="ltr-fact-label plain">Resolution date</span>
          <div className="ltr-strong">{fmtDateTime(c.closedAt)}</div>
        </div>
        <div>
          <span className="ltr-fact-label plain">Time to resolve</span>
          <div className="ltr-strong">{durationLabel}</div>
        </div>
      </div>
      {latestAppointment && latestAppointment.prescriptionUrl && (
        <div className="ltr-doc-row">
          <span className="ltr-strong">Rx-{latestAppointment.prescriptionId}</span>
          <span className="ltr-cell-sub">
            Issued {fmtDateTime(latestAppointment.prescribedAt)} by {nameOf(names, latestAppointment.prescribedBy)}
          </span>
          <DocumentLink url={latestAppointment.prescriptionUrl} />
        </div>
      )}
    </section>
  );

  const unlinkedBanner = unlinked && (
    <div className="ltr-alert">
      <FontIcon>error_outline</FontIcon>
      <span>
        Appointment <strong>{unlinked.link.appointmentId}</strong> was booked but could not be recorded on this case
        {unlinked.message ? ` (${unlinked.message})` : ""}. Do not book it again.
      </span>
      <button
        type="button"
        className="ltr-btn small primary"
        onClick={() =>
          linkBooking(unlinked.link).catch((err) => setUnlinked({ ...unlinked, message: err?.message || "" }))
        }
      >
        Retry
      </button>
    </div>
  );

  // Intake puts the patient on the DC account and then the LTR plan; until
  // both have landed a consultation cannot be booked on the LTR plan.
  const programmeIncomplete = !isClosed && (!c.accountAssigned || !c.planAssigned);
  const programmeBanner = programmeIncomplete && (
    <div className="ltr-alert">
      <FontIcon>error_outline</FontIcon>
      <span>
        This patient is not on the LTR programme yet — DC account{c.dcAccountCode ? ` ${c.dcAccountCode}` : ""}{" "}
        {c.accountAssigned ? "assigned" : "not assigned"}, plan{c.planId ? ` ${c.planId}` : ""}{" "}
        {c.planAssigned ? "assigned" : "not assigned"} — so a consultation cannot be booked on the LTR plan.
      </span>
      <button
        type="button"
        className="ltr-btn small primary"
        disabled={retryingProgramme}
        onClick={() => {
          setRetryingProgramme(true);
          mutate(call(ltrService.retryProgramme, c.id), "Patient put on the LTR programme")
            .catch((err) =>
              EventService.emit("showError", {
                message: err?.message || "Could not put the patient on the LTR programme.",
              }),
            )
            .finally(() => setRetryingProgramme(false));
        }}
      >
        {retryingProgramme ? "Retrying..." : "Retry"}
      </button>
    </div>
  );

  const remarkCall = remarkFor && (c.calls || []).find((x) => x.callId === remarkFor.callId);

  return (
    <div className="ltr-detail-container" ref={containerRef}>
      {pinnedHeader}
      {hero}
      {programmeBanner}
      {aiSummary}
      {clinicalSection}
      {recommendedSection}
      {coverageSection}
      {closureSection}
      <CallAudit
        caseRecord={c}
        names={names}
        isClosed={isClosed}
        onAssignTask={() => patient && setNoteOpen(true)}
        onConvert={(callRow) => setConvertCall(callRow)}
        onRemark={(callRow) => setRemarkFor(callRow)}
      />
      {unlinkedBanner}
      <RequiredAction
        caseRecord={c}
        names={names}
        isClosed={isClosed}
        rxPendingFor={rxWatch && rxWatch.appointmentId}
        onCreateRx={startRx}
        onCheckRx={() => checkRx(true)}
        onAssignAction={(appt) => setAssignAction({ appointment: appt, withFollowUp: true })}
      />
      <ActivityLog
        audits={ltrAudits}
        roleById={roleById}
        canViewEvaluation={evaluationVersions.length > 0}
        onViewEvaluation={() => setSummaryAuditId(null)}
      />

      {/* ---- AI evaluation ---- */}
      <AIEvaluationDialog
        open={!!evalDialog}
        mode={evalDialog?.mode}
        record={evalDialog?.record}
        evaluatorName={currentUserName}
        onClose={() => setEvalDialog(null)}
        onSaved={handleEvaluationSaved}
      />
      <EvaluationSummaryDialog
        open={summaryAuditId !== undefined}
        record={riskRecord}
        audits={riskAudits}
        initialAuditId={summaryAuditId}
        patientId={c.patientId}
        onClose={() => setSummaryAuditId(undefined)}
      />
      <Dialog
        open={!!prevSummaries}
        onClose={() => setPrevSummaries(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: "ltr-modal" }}
      >
        <div className="ltr-modal-header">
          <div>
            <h3>Previous AI summary</h3>
            <p>Earlier assessments for PID-{c.patientId}</p>
          </div>
          <button type="button" className="ltr-modal-close" onClick={() => setPrevSummaries(null)} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="ltr-modal-body">
          {prevSummaries?.loading ? (
            <p className="ltr-muted">Loading...</p>
          ) : !prevSummaries?.items?.length ? (
            <p className="ltr-muted">No previous assessments found.</p>
          ) : (
            <ul className="ltr-prev-list">
              {prevSummaries.items.map((item) => (
                <li key={item.id}>
                  <button type="button" onClick={() => openPreviousSummary(item)}>
                    <span>{moment(item.triggeredAt).format("D MMM YYYY")}</span>
                    <RiskBadge level={item.riskLevel} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Dialog>

      {/* ---- calling ---- */}
      <CallDetailsDialog
        open={callDetailsOpen}
        patient={patient}
        caseRecord={c}
        onClose={() => setCallDetailsOpen(false)}
        onCallPlaced={handleCallPlaced}
      />
      <RemarkDialog
        open={!!remarkFor}
        callLabel={remarkCall ? `Call ${remarkCall.callId} · ${fmtDateTime(remarkCall.startedAt)}` : ""}
        initial={remarkCall ? remarkCall.remark : ""}
        onClose={() => setRemarkFor(null)}
        onSubmit={(remark) => mutate(call(ltrService.updateCall, c.id, remarkFor.callId, { remark }), "Remark saved")}
      />
      <ComingSoonDialog
        open={!!comingSoon}
        kind={comingSoon}
        onClose={() => setComingSoon(null)}
        onCall={() => {
          setComingSoon(null);
          if (!isClosed && patient && !busyOnAnotherCall) setCallDetailsOpen(true);
        }}
      />

      {/* ---- required action ---- */}
      <ConvertCallDialog
        open={!!convertCall}
        caseRecord={c}
        call={convertCall}
        patient={patient}
        names={names}
        onClose={() => setConvertCall(null)}
        onBooked={handleBooked}
      />
      <AppointmentCreatedDialog
        open={!!createdAppointment}
        appointment={createdAppointment}
        onClose={() => setCreatedAppointment(null)}
        onCreateRx={() => {
          const appt = createdAppointment;
          setCreatedAppointment(null);
          startRx(appt);
        }}
      />
      <AssignActionDialog
        open={!!assignAction}
        appointment={assignAction?.appointment}
        withFollowUp={!!assignAction?.withFollowUp}
        defaultAssignee={
          c.assignedToRole === ROLE_COORDINATOR && c.assignedToEmployeeId ? { id: c.assignedToEmployeeId } : null
        }
        onClose={() => setAssignAction(null)}
        onSubmit={(body) => handleSetActions(assignAction.appointment, body)}
      />
      <FollowUpTaskDialog
        open={!!followUpOpen}
        title={followUpOpen === "assign" ? "Assign case" : undefined}
        defaultAssignee={
          c.assignedToEmployeeId ? { id: c.assignedToEmployeeId, role: c.assignedToRole || ROLE_DOCTOR } : null
        }
        // Nobody holds the case yet: offer the doctors first.
        defaultRole={ROLE_DOCTOR}
        onClose={() => setFollowUpOpen(null)}
        onSubmit={handleFollowUpTask}
      />
      {noteOpen && patient && (
        <Suspense fallback={null}>
          <LTRProgressNote patient={patient} onClose={() => setNoteOpen(false)} />
        </Suspense>
      )}

      {/* ---- closing ---- */}
      <CloseCaseDialog
        open={closeOpen}
        onClose={() => setCloseOpen(false)}
        onSubmit={(body) =>
          mutate(call(ltrService.closeCase, c.id, body)).then(() => {
            setClosedOpen(true);
          })
        }
      />
      <CaseClosedDialog
        open={closedOpen}
        caseRecord={c}
        patientName={patientName}
        closedByName={nameOf(names, c.closedBy)}
        durationLabel={durationLabel}
        onStay={() => setClosedOpen(false)}
        onBack={() => {
          setClosedOpen(false);
          goBack();
        }}
      />
      <ClosureDetailsDialog
        open={closureOpen}
        caseRecord={c}
        patientName={patientName}
        closedByName={nameOf(names, c.closedBy)}
        closedByRole={ROLE_LABEL_MAP[roleById[c.closedBy]] || ""}
        durationLabel={durationLabel}
        actionLabel={latestActions.map((a) => ACTION_LABEL_MAP[a]).join(", ")}
        onClose={() => setClosureOpen(false)}
        onShowDetails={() => {
          setClosureOpen(false);
          if (closureRef.current) closureRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
      />
    </div>
  );
}

export default LTRDetail;
